import { getRouteApi } from '@tanstack/react-router';
import type { AppRouterContext } from 'providers/AppRouter';

const rootRoute = getRouteApi('__root__');

/**
 * Hook that returns the root application router context.
 *
 * Provides access to application-level services and utilities including:
 * - `queryClient`: TanStack React Query client for server state management
 * - `flags`: Feature flags for conditional rendering and A/B testing
 * - `steps`: Signup step tracking and navigation
 * - `snackbarContext`: Toast notification management
 * - `localization`: i18n configuration and language settings
 * - `eventTracking`: Event tracking and analytics
 * - `rum`: Real User Monitoring (RUM) service for performance monitoring
 * - `growthbook`: GrowthBook integration for feature flags and experiments
 *
 * This hook must be called in route components or components rendered within
 * routes. It will throw if the router context is not available.
 *
 * @returns {AppRouterContext} The root router context object containing all application services.
 *
 * @example
 * ```tsx
 * import { useRootContext } from 'router/hooks/useRootContext';
 *
 * export function MyComponent() {
 *   const { queryClient, localization, eventTracking } = useRootContext();
 *
 *   return <div>Locale: {localization.language}</div>;
 * }
 * ```
 *
 * @see {@link AppRouterContext} for the context interface definition
 * @see {@link https://tanstack.com/router/latest/docs/guide/router-context} for TanStack Router context documentation
 */
export function useRootContext(): AppRouterContext {
  return rootRoute.useRouteContext();
}
